import { type ReactNode } from 'react';
import { ActionBoardStoreProvider, usePersistActionBoardStore } from '@/actionBoards/actionBoard.store';
import { AdministrationStoreProvider } from '@/administration/administration.store';
import { AgentBuilderStoreProvider } from '@/agentBuilder/agentBuilder.store';
import { AgentBuilderRunStoreProvider } from '@/agentBuilder/run/agentBuilderRun.store';
import { AnnotationStoreProvider, usePersistAnnotationStore } from '@/annotation/annotation.store';
import {
  sqAnnotationStore,
  sqClusteringStoreV2,
  sqCursorStore,
  sqDigitalFilterStore,
  sqDisplayToolStore,
  sqExploreFolderModalStore,
  sqExploreWorkbookModalStore,
  sqIsolationForestStoreV2,
  sqOnnxStore,
  sqOperationalizeStatisticsSummaryStore,
  sqPartialLeastSquaresStoreV2,
  sqPdfExportStore,
  sqPrincipalComponentAnalysisStoreV2,
  sqScorecardStore,
  sqSearchStore,
  sqSelfOrganizingMapsStoreV2,
  sqStateSynchronizer,
  sqToolTemplateStore,
  sqWorkstepsStore,
} from '@/core/core.stores';
import { DashboardStoreProvider, usePersistDashboardStore } from '@/dashboard/dashboard.store';
import {
  ExploreFolderModalStoreProvider,
  usePersistExploreFolderModalStore,
} from '@/explorer/exploreFolderModal.store';
import {
  ExploreWorkbookModalStoreProvider,
  usePersistExploreWorkbookModalStore,
} from '@/explorer/exploreWorkbookModal.store';
import { AddOnToolStoreProvider } from '@/homescreen/addOnTool.store';
import { ImpactReportStoreProvider, usePersistImpactReportStore } from '@/impactReports/impactReports.store';
import { WorkbenchLayoutStoreProvider, usePersistWorkbenchLayoutStore } from '@/main/workbenchLayout.store';
import { PdfExportStoreProvider, usePersistPdfExportStore } from '@/reportEditor/pdfExport.store';
import { SearchStoreProvider, usePersistSearchStore } from '@/search/search.store';
import { PersistenceProvider } from '@/stores/PersistenceProvider';
import { SystemWarningStoreProvider } from '@/systemWarning/systemWarning.store';
import { ScorecardStoreProvider, usePersistScorecardStore } from '@/tableBuilder/scorecard.store';
import { ClusteringV2StoreProvider, usePersistClusteringV2Store } from '@/tools/clusteringV2/clustering.store';
import { DigitalFilterStoreProvider, usePersistDigitalFilterStore } from '@/tools/digitalFilter/digitalFilter.store';
import { DisplayToolStoreProvider, usePersistDisplayToolStore } from '@/tools/display/displayTool.store';
import {
  IsolationForestV2StoreProvider,
  usePersistIsolationForestV2Store,
} from '@/tools/isolationForestV2/isolationForest.store';
import { ToolTemplateStoreProvider, usePersistToolTemplateStore } from '@/tools/models/toolTemplate.store';
import { OnnxStoreProvider, usePersistOnnxToolStore } from '@/tools/onnx/onnx.store';
import {
  OperationalizeStatsSummaryStoreProvider,
  usePersistOperationalizeStatsSummaryStore,
} from '@/tools/operationalizeStatisticsSummary/operationalizeStatisticsSummary.store';
import {
  PartialLeastSquaresV2StoreProvider,
  usePersistPartialLeastSquaresV2Store,
} from '@/tools/partialLeastSquaresV2/partialLeastSquares.store';
import {
  PrincipalComponentAnalysisV2StoreProvider,
  usePersistPrincipalComponentAnalysisV2Store,
} from '@/tools/principalComponentAnalysisV2/principalComponentAnalysis.store';
import {
  SelfOrganizingMapsV2StoreProvider,
  usePersistSelfOrganizingMapsV2Store,
} from '@/tools/selfOrganizingMapsV2/selfOrganizingMaps.store';
import { CursorStoreProvider, usePersistCursorStore } from '@/trendData/cursor.store';
import { VantageTrendStoreProvider, usePersistVantageTrendStore } from '@/vantage/vantageTrend.store';
import { VantageWorkbookStoreProvider } from '@/vantage/vantageWorkbook.store';
import { WorkstepsStoreProvider } from '@/worksteps/worksteps.store';

export const PersistStores = () => {
  // NOTE: this is where each store that needs to be persisted should be added using its custom hook.
  usePersistImpactReportStore();
  usePersistActionBoardStore();
  usePersistAnnotationStore();
  usePersistCursorStore();
  usePersistVantageTrendStore();
  usePersistOperationalizeStatsSummaryStore();
  usePersistPdfExportStore();
  usePersistSearchStore();
  usePersistExploreFolderModalStore();
  usePersistExploreWorkbookModalStore();
  usePersistDisplayToolStore();
  usePersistToolTemplateStore();
  usePersistScorecardStore();
  usePersistWorkbenchLayoutStore();
  usePersistDigitalFilterStore();
  usePersistOnnxToolStore();
  usePersistDashboardStore();
  usePersistClusteringV2Store();
  usePersistIsolationForestV2Store();
  usePersistPartialLeastSquaresV2Store();
  usePersistPrincipalComponentAnalysisV2Store();
  usePersistSelfOrganizingMapsV2Store();

  return null;
};

export const StoreProviders = ({ children }: { children: ReactNode }) => {
  return (
    <PersistenceProvider stateSynchronizer={sqStateSynchronizer}>
      <AdministrationStoreProvider>
        <ImpactReportStoreProvider>
          <VantageWorkbookStoreProvider>
            <DigitalFilterStoreProvider store={sqDigitalFilterStore}>
              <WorkbenchLayoutStoreProvider>
                <ToolTemplateStoreProvider store={sqToolTemplateStore}>
                  <SearchStoreProvider store={sqSearchStore}>
                    <ExploreFolderModalStoreProvider store={sqExploreFolderModalStore}>
                      <ExploreWorkbookModalStoreProvider store={sqExploreWorkbookModalStore}>
                        <ActionBoardStoreProvider>
                          <SystemWarningStoreProvider>
                            <AnnotationStoreProvider store={sqAnnotationStore}>
                              <CursorStoreProvider store={sqCursorStore}>
                                <AddOnToolStoreProvider>
                                  <AgentBuilderRunStoreProvider>
                                    <AgentBuilderStoreProvider>
                                      <VantageTrendStoreProvider>
                                        <PdfExportStoreProvider store={sqPdfExportStore}>
                                          <WorkstepsStoreProvider store={sqWorkstepsStore}>
                                            <DisplayToolStoreProvider store={sqDisplayToolStore}>
                                              <ClusteringV2StoreProvider store={sqClusteringStoreV2}>
                                                <IsolationForestV2StoreProvider store={sqIsolationForestStoreV2}>
                                                  <PartialLeastSquaresV2StoreProvider
                                                    store={sqPartialLeastSquaresStoreV2}>
                                                    <PrincipalComponentAnalysisV2StoreProvider
                                                      store={sqPrincipalComponentAnalysisStoreV2}>
                                                      <SelfOrganizingMapsV2StoreProvider
                                                        store={sqSelfOrganizingMapsStoreV2}>
                                                        <OnnxStoreProvider store={sqOnnxStore}>
                                                          <OperationalizeStatsSummaryStoreProvider
                                                            store={sqOperationalizeStatisticsSummaryStore}>
                                                            <ScorecardStoreProvider store={sqScorecardStore}>
                                                              <DashboardStoreProvider>
                                                                <PersistStores />
                                                                {children}
                                                              </DashboardStoreProvider>
                                                            </ScorecardStoreProvider>
                                                          </OperationalizeStatsSummaryStoreProvider>
                                                        </OnnxStoreProvider>
                                                      </SelfOrganizingMapsV2StoreProvider>
                                                    </PrincipalComponentAnalysisV2StoreProvider>
                                                  </PartialLeastSquaresV2StoreProvider>
                                                </IsolationForestV2StoreProvider>
                                              </ClusteringV2StoreProvider>
                                            </DisplayToolStoreProvider>
                                          </WorkstepsStoreProvider>
                                        </PdfExportStoreProvider>
                                      </VantageTrendStoreProvider>
                                    </AgentBuilderStoreProvider>
                                  </AgentBuilderRunStoreProvider>
                                </AddOnToolStoreProvider>
                              </CursorStoreProvider>
                            </AnnotationStoreProvider>
                          </SystemWarningStoreProvider>
                        </ActionBoardStoreProvider>
                      </ExploreWorkbookModalStoreProvider>
                    </ExploreFolderModalStoreProvider>
                  </SearchStoreProvider>
                </ToolTemplateStoreProvider>
              </WorkbenchLayoutStoreProvider>
            </DigitalFilterStoreProvider>
          </VantageWorkbookStoreProvider>
        </ImpactReportStoreProvider>
      </AdministrationStoreProvider>
    </PersistenceProvider>
  );
};
